import React from "react";
import { Navigate, useLocation } from "react-router-dom";

import PublicRoute from "./services/auth/PublicRoute";
import ProtectedRoute from "./services/auth/ProtectedRoute";
import ErrorBoundary from "./component/errorBoundaryComponent/ErrorBoundary";

const Login = React.lazy(() => import("./views/pages/login/Login"));
const Register = React.lazy(() => import("./views/pages/register/Register"));
const CheckEmail = React.lazy(() => import("./views/pages/forgetPassword/CheckEmail"));
const CreatePassword = React.lazy(() => import("./views/pages/forgetPassword/CreatePassword"));
const PasswordReset = React.lazy(() => import("./views/pages/forgetPassword/PasswordReset"));
const SetupWelcome = React.lazy(() => import("./views/pages/setepForm/SetupWelcome"));
const PaymentPage = React.lazy(() => import("./views/pages/setepForm/PaymentPage"));
const AllSites = React.lazy(() => import("./views/pages/sites/AllSites"));
const SiteDetails = React.lazy(() => import("./views/pages/sites/SiteDetails"));
const SiteFinancialPage = React.lazy(() => import("./views/pages/sites/SiteFinancialPage"));
const ExpenseCatalogPage = React.lazy(() => import("./views/pages/expenseCatalog/ExpenseCatalogPage"));
const Unit = React.lazy(() => import("./views/pages/units/Unit"));
const SettingPage = React.lazy(() => import("./views/pages/settings/settings"));
const OpportunityCalculator = React.lazy(() => import("./views/pages/opportunityCalculator/OpportunityCalculator"));
const SensorTesting = React.lazy(() => import("./views/pages/sensors/SensorTesting"));
const EnergyIntelligence = React.lazy(() => import("./views/pages/energy/EnergyIntelligence"));

const RouteErrorBoundary = ({ children }) => {
  const location = useLocation();

  return <ErrorBoundary resetKey={`${location.pathname}${location.search}`}>{children}</ErrorBoundary>;
};

const withBoundary = element => <RouteErrorBoundary>{element}</RouteErrorBoundary>;

const publicPage = element => <PublicRoute>{withBoundary(element)}</PublicRoute>;

const protectedPage = element => <ProtectedRoute requiredRole="user">{withBoundary(element)}</ProtectedRoute>;

const routes = [
  {
    path: "/",
    name: "Login",
    element: publicPage(<Login />),
  },
  {
    path: "/register",
    name: "Register",
    element: publicPage(<Register />),
  },
  {
    path: "/check-email",
    name: "CheckEmail",
    element: publicPage(<CheckEmail />),
  },
  {
    path: "/create-password",
    name: "CreatePassword",
    element: publicPage(<CreatePassword />),
  },
  {
    path: "/password-reset",
    name: "PasswordReset",
    element: publicPage(<PasswordReset />),
  },
  {
    path: "/welcome",
    name: "SetupWelcome",
    element: withBoundary(<SetupWelcome />),
  },
  {
    path: "/card-details",
    name: "PaymentPage",
    element: withBoundary(<PaymentPage />),
  },
  {
    path: "/sites",
    name: "AllSites",
    element: protectedPage(<AllSites />),
  },
  {
    path: "/site-management",
    name: "LegacySitesRedirect",
    element: protectedPage(<Navigate to="/sites" replace />),
  },
  {
    path: "/unit-management",
    name: "LegacyUnitsRedirect",
    element: protectedPage(<Navigate to="/sites" replace />),
  },
  {
    path: "/site/:siteId",
    name: "SiteDetails",
    element: protectedPage(<SiteDetails />),
  },
  {
    path: "/site/:siteId/units",
    name: "SiteUnits",
    element: protectedPage(<SiteDetails />),
  },
  {
    path: "/site/:siteId/settings",
    name: "SiteSettings",
    element: protectedPage(<SiteDetails />),
  },
  {
    path: "/site/:siteId/expense-catalogue",
    name: "SiteExpenseCatalogue",
    element: protectedPage(<ExpenseCatalogPage />),
  },
  {
    path: "/site/:siteId/:financialFeature",
    name: "SiteFinancialArea",
    element: protectedPage(<SiteFinancialPage />),
  },
  {
    path: "/site/:siteId/energy",
    name: "EnergyIntelligence",
    element: protectedPage(<EnergyIntelligence />),
  },
  {
    path: "/unit/:unitId",
    name: "Unit",
    element: protectedPage(<Unit />),
  },
  {
    path: "/opportunity-calculator",
    name: "OpportunityCalculator",
    element: protectedPage(<OpportunityCalculator />),
  },
  {
    path: "/settings",
    name: "SettingsPage",
    element: protectedPage(<SettingPage />),
  },
  {
    path: "/sensor-testing",
    name: "SensorTesting",
    element: protectedPage(<SensorTesting />),
  },
  {
    path: "*",
    name: "Fallback",
    element: <Navigate to="/" replace />,
  },
];

export default routes;
